import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReCAPTCHA from 'react-google-recaptcha';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';
import { bookingApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Schedule = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableDatesLoading, setAvailableDatesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [appointmentType, setAppointmentType] = useState('firstAppointment');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
    discountCode: '',
    appointmentType: 'firstAppointment',
  });

  const steps = [t('schedule.selectDate'), t('schedule.personalInfo'), t('common.submit'), t('schedule.checkEmail')];

  // Get discount code from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    const discountMatch = hash.match(/#discount=([^&]*)/);
    if (discountMatch) {
      setFormData(prev => ({ ...prev, discountCode: discountMatch[1] }));
    }
  }, []);

  // Load available dates when component mounts
  useEffect(() => {
    loadAvailableDates();
  }, []);

  // Load available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots(selectedDate);
    }
  }, [selectedDate, formData.appointmentType]);

  const loadAvailableDates = async () => {
    setAvailableDatesLoading(true);
    try {
      const startDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
      const endDate = dayjs().add(3, 'month').format('YYYY-MM-DD');
      
      console.log('Loading available dates from', startDate, 'to', endDate);
      const dates = await bookingApi.getAvailableDates(startDate, endDate);
      console.log('Received available dates:', dates);
      
      setAvailableDates(dates);
    } catch (err) {
      console.error('Error loading available dates:', err);
      // Don't show error for available dates loading as it's not critical - we can fallback to normal date picker
    } finally {
      setAvailableDatesLoading(false);
    }
  };

  const loadAvailableSlots = async (date) => {
    setLoading(true);
    setError('');
    try {
      console.log('Loading availability for date:', date.format('YYYY-MM-DD'));
      const slots = await bookingApi.getAvailability(date.format('YYYY-MM-DD'), {appointmentDuration: {firstAppointment: 105, followUpAppointment: 75}[formData.appointmentType]});
      console.log('Received slots from API:', slots);
      
      // Backend returns array of time strings, convert to objects with id and time
      // Ensure consistent 24-hour format
      const formattedSlots = slots.map((timeString) => {
        // Parse and reformat to ensure consistent 24-hour format
        const formattedTime = dayjs(`1970-01-01T${timeString}:00`).format('HH:mm');
        return {
          id: `${date.format('YYYY-MM-DD')}-${formattedTime}`,
          time: formattedTime
        };
      }).sort((a, b) => a.time.localeCompare(b.time));
      
      console.log('Formatted slots:', formattedSlots);
      setAvailableSlots(formattedSlots);
    } catch (err) {
      console.error('Error loading slots:', err);
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setActiveStep(1);
  };

  const handleFormChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    console.log('Form data:',field, formData);
  };

  const handleSubmit = async () => {
    // if (!captchaToken) {
    //   setError('Please complete the captcha');
    //   return;
    // }

    setLoading(true);
    setError('');

    try {
      console.log('Submitting booking with data:', {
        date: selectedDate.format('YYYY-MM-DD'),
        time: selectedSlot.time,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.notes,
        discountCode: formData.discountCode,
        appointmentType: t(`schedule.${formData.appointmentType}`),
        appointmentDuration: {firstAppointment: 105, followUpAppointment: 75}[formData.appointmentType],
        recaptchaToken: captchaToken
      });

      const appointmentData = {
        date: selectedDate.format('YYYY-MM-DD'),
        time: selectedSlot.time,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.notes,
        discountCode: formData.discountCode,
        appointmentType: t(`schedule.${formData.appointmentType}`),
        appointmentDuration: {firstAppointment: 105, followUpAppointment: 75}[formData.appointmentType],
        recaptchaToken: captchaToken
      };

      const result = await bookingApi.bookAppointment(appointmentData);
      console.log('Booking result:', result);
      
      // Move to success step instead of redirecting immediately
      setActiveStep(3);
    } catch (err) {
      console.error('Error booking appointment:', err);
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Function to check if a date should be disabled in the date picker
  const shouldDisableDate = (date) => {
    const dateString = date.format('YYYY-MM-DD');
    
    // If we're still loading available dates, don't disable any dates yet
    if (availableDatesLoading) {
      return false;
    }
    
    // If we have available dates loaded, only allow those dates
    if (availableDates.length > 0) {
      return !availableDates.includes(dateString);
    }
    
    // Fallback: if no available dates loaded (error case), allow all future dates
    return false;
  };

  const canProceedToNext = () => {
    if (activeStep === 0) return selectedDate && selectedSlot;
    if (activeStep === 1) return formData.firstName && formData.lastName && formData.email;
    return false;
  };

  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
        <Header />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h2" align="center" sx={{ mb: 4 }}>
          {t('schedule.title')}
        </Typography>

        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            maxWidth: '100%',
            '& .MuiStepConnector-root': {
              display: { xs: 'none', sm: 'block' }
            },
            '& .MuiStep-root': {
              minWidth: { xs: 'auto', sm: '130px' },
              px: { xs: 0.5, sm: 1 }
            }
          }}
          orientation={isMobile ? 'vertical' : 'horizontal'}
        >
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel 
                sx={{ 
                  '& .MuiStepLabel-label': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2,
                    textAlign: { xs: 'left', sm: 'center' }
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Date and Time Selection */}
        {activeStep === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('schedule.appointmentType')}</Typography>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="firstAppointment"
                  name="radio-buttons-group"
                  onChange={(e) => {
                    setAppointmentType(e.target.value);
                    handleFormChange('appointmentType')(e);
                  }}
                  value={appointmentType}
                >
                  <FormControlLabel value="firstAppointment" control={<Radio />} label={t('schedule.firstAppointment')} />
                  <FormControlLabel value="followUpAppointment" control={<Radio />} label={t('schedule.followUpAppointment')} />
                </RadioGroup>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('schedule.selectDate')}
                </Typography>
                
                {availableDatesLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading available dates...
                    </Typography>
                  </Box>
                )}
                
                {!availableDatesLoading && availableDates.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      No available dates found. Please check back later or contact us directly.
                    </Typography>
                  </Alert>
                )}
                
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  minDate={dayjs().add(1, 'day')}
                  maxDate={dayjs().add(3, 'month')}
                  shouldDisableDate={shouldDisableDate}
                  sx={{ width: '100%' }}
                  disabled={availableDatesLoading}
                />
                
                {!availableDatesLoading && availableDates.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Only dates with available appointments are shown
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('schedule.selectTime')}
                </Typography>
                
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!selectedDate && (
                  <Typography color="text.secondary">
                    {t('schedule.selectDate')}
                  </Typography>
                )}

                {selectedDate && !loading && availableSlots.length === 0 && (
                  <Typography color="text.secondary">
                    {t('schedule.noSlotsAvailable')}
                  </Typography>
                )}

                {availableSlots.length > 0 && (
                  <Grid container spacing={1}>
                    {availableSlots.map((slot) => (
                      <Grid item xs={6} sm={4} key={slot.id}>
                        <Chip
                          label={slot.time}
                          clickable
                          color={selectedSlot?.id === slot.id ? 'primary' : 'default'}
                          onClick={() => handleSlotSelect(slot)}
                          sx={{ 
                            width: '100%',
                            justifyContent: 'center',
                            py: 1
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Step 2: Personal Information */}
        {activeStep === 1 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('schedule.personalInfo')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('schedule.firstName')}
                  value={formData.firstName}
                  onChange={handleFormChange('firstName')}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('schedule.lastName')}
                  value={formData.lastName}
                  onChange={handleFormChange('lastName')}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('schedule.email')}
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange('email')}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('schedule.phone')}
                  value={formData.phone}
                  onChange={handleFormChange('phone')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('schedule.notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleFormChange('notes')}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('schedule.discountCode')}
                  value={formData.discountCode}
                  onChange={handleFormChange('discountCode')}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setActiveStep(0)}>
                {t('common.back')}
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(2)}
                disabled={!canProceedToNext()}
              >
                {t('common.next')}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 3: Confirmation and Captcha */}
        {activeStep === 2 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('common.submit')}
            </Typography>

            {/* Appointment Summary */}
            <Card sx={{ mb: 3, backgroundColor: 'background.default' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Appointment Summary
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.date')}:</strong> {selectedDate?.format('DD.MM.YYYY')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.time')}:</strong> {selectedSlot?.time}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.name')}:</strong> {formData.firstName} {formData.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {formData.email}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.appointmentType')}:</strong> {t(`schedule.${formData.appointmentType}`)}
                </Typography>
              </CardContent>
            </Card>

            

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setActiveStep(1)}>
                {t('common.back')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {t('schedule.bookAppointment')}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 4: Email Confirmation */}
        {activeStep === 3 && (
          <Paper sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            textAlign: 'center',
            mx: { xs: 1, sm: 0 }
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ mb: 3, color: 'primary.main' }}
            >
              📧 {t('schedule.requestSent')}
            </Typography>
            
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3, 
                textAlign: 'left',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                sx={{ mb: 2 }}
              >
                {t('schedule.confirmationRequired')}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  wordBreak: 'break-word'
                }}
              >
                {t('schedule.emailSentTo')} <strong>{formData.email}</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {t('schedule.checkEmailInstructions')}
              </Typography>
            </Alert>

            <Card sx={{ 
              mb: 3, 
              backgroundColor: 'background.default',
              mx: { xs: 0, sm: 1 }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  sx={{ mb: 2 }}
                >
                  {t('schedule.appointmentDetails')}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1,
                  textAlign: 'left'
                }}>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    <strong>{t('schedule.appointmentType')}:</strong> {t(`schedule.${formData.appointmentType}`)}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    <strong>{t('schedule.date')}:</strong> {selectedDate?.format('DD.MM.YYYY')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    <strong>{t('schedule.time')}:</strong> {selectedSlot?.time}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    <strong>{t('schedule.name')}:</strong> {formData.firstName} {formData.lastName}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 1,
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    {t('schedule.pendingStatus')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: { xs: 1, sm: 2 }, 
              flexDirection: { xs: 'column', sm: 'row' },
              px: { xs: 1, sm: 0 }
            }}>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                sx={{ 
                  minWidth: { xs: 'auto', sm: '140px' },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
                onClick={() => {
                  setActiveStep(0);
                  setSelectedDate(null);
                  setSelectedSlot(null);
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    appointmentType: appointmentType,
                    notes: '',
                    discountCode: formData.discountCode, // Keep discount code
                  });
                }}
              >
                {t('schedule.bookAnother')}
              </Button>
              <Button
                variant="contained"
                fullWidth={isMobile}
                href="/"
                sx={{ 
                  minWidth: { xs: 'auto', sm: '140px' },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                {t('schedule.backToHome')}
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    <Footer />
    </Box>
  );
};

export default Schedule; 